import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Check, Zap, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { startCheckout } from '@/lib/razorpay';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState('free');

  useEffect(() => {
    axios.get(`${API}/plans`).then(res => setPlans(res.data)).catch(console.error).finally(() => setLoading(false));
    if (user?.subscription?.plan) setCurrentPlan(user.subscription.plan);
  }, [user]);

  const handleSubscribe = async (planId) => {
    if (!user) {
      const redirectUrl = window.location.origin + '/pricing';
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (planId === 'free') return;
    if (planId === 'enterprise') { window.location.href = '/org'; return; }
    if (planId === 'pro') {
      await startCheckout({
        type: 'pro',
        onSuccess: async () => {
          setCurrentPlan('pro');
          toast.success('Pro activated for 30 days');
          if (refreshUser) await refreshUser();
        }
      });
      return;
    }
    try {
      await axios.post(`${API}/subscribe`, { plan: planId }, { withCredentials: true });
      setCurrentPlan(planId);
      toast.success(`Subscribed to ${planId} plan`);
    } catch { toast.error('Failed to subscribe'); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="mono-data text-lg animate-pulse-red">Loading Plans...</div></div>;
  }

  return (
    <div className="min-h-screen" data-testid="pricing-page">
      {/* Header */}
      <div className="border-b border-[#09090B] py-16 md:py-24">
        <div className="container-swiss text-center">
          <p className="mono-data text-[#09090B] mb-4">Pricing</p>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="pricing-heading">
            Invest in Your Brain
          </h1>
          <p className="text-lg text-zinc-600 max-w-xl mx-auto">
            Choose the plan that matches your training goals. All plans include the session player engine with real-time scoring.
          </p>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="container-swiss py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-[#09090B] max-w-5xl mx-auto">
          {plans.map(plan => {
            const isCurrent = currentPlan === plan.plan_id;
            const isPopular = plan.popular;
            return (
              <div
                key={plan.plan_id}
                className={`border-r border-b border-[#09090B] flex flex-col ${isPopular ? 'bg-[#09090B] text-white' : 'bg-white'}`}
                data-testid={`plan-card-${plan.plan_id}`}
              >
                {/* Popular badge */}
                {isPopular && (
                  <div className="bg-[#09090B] text-white text-center py-2 mono-data text-xs tracking-widest" data-testid="popular-badge">
                    Most Popular
                  </div>
                )}

                <div className="p-8 flex-1 flex flex-col">
                  {/* Plan name */}
                  <h3 className="text-xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="mb-6">
                    {plan.price > 0 ? (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                          ₹{plan.price}
                        </span>
                        <span className={`mono-data text-xs ${isPopular ? 'text-zinc-400' : 'text-zinc-500'}`}>
                          /{plan.period}
                        </span>
                      </div>
                    ) : plan.plan_id === 'enterprise' ? (
                      <span className="text-2xl font-black tracking-tighter" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Custom
                      </span>
                    ) : (
                      <span className="text-4xl font-black tracking-tighter" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                        ₹0
                      </span>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8 flex-1">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isPopular ? 'text-white' : 'text-[#09090B]'}`} />
                        <span className={isPopular ? 'text-zinc-300' : 'text-zinc-600'}>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  {plan.plan_id === 'enterprise' ? (
                    <Link to="/org" className={`text-center py-4 font-bold tracking-widest uppercase text-sm border transition-colors ${isPopular ? 'border-white text-white hover:bg-white hover:text-[#09090B]' : 'border-[#09090B] hover:bg-[#09090B] hover:text-white'}`} data-testid={`plan-cta-${plan.plan_id}`}>
                      <span className="flex items-center justify-center gap-2"><Building2 className="w-4 h-4" /> Open Portal</span>
                    </Link>
                  ) : isCurrent ? (
                    <div className={`text-center py-4 font-bold tracking-widest uppercase text-sm border ${isPopular ? 'border-zinc-600 text-zinc-400' : 'border-zinc-300 text-zinc-400'}`} data-testid={`plan-current-${plan.plan_id}`}>
                      Current Plan
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSubscribe(plan.plan_id)}
                      className={`w-full py-4 font-bold tracking-widest uppercase text-sm border transition-colors ${
                        isPopular
                          ? 'bg-[#09090B] border-[#09090B] text-white hover:bg-white hover:text-[#09090B]'
                          : 'border-[#09090B] hover:bg-[#09090B] hover:text-white'
                      }`}
                      data-testid={`plan-cta-${plan.plan_id}`}
                    >
                      <span className="flex items-center justify-center gap-2"><Zap className="w-4 h-4" /> {plan.cta}</span>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust note */}
        <p className="text-center text-xs text-zinc-400 mt-8 mono-data">
          Secure payments by Razorpay · Cancel anytime · GST invoice on request
        </p>
      </div>
    </div>
  );
}
